/**
 * @typedef {ReturnType<typeof import('widgets/forms/BasicInput').default>} BasicInput
 * @typedef {InstanceType<typeof import('widgets/toolbox/RefElement').RefElement>} RefElement
 */

/**
 * @description Base InputPassword implementation
 * @param {BasicInput} BasicInput Base widget for extending
 * @returns {typeof InputPassword} Input Password class
 */
export default function (BasicInput) {
    /**
     * @category widgets
     * @subcategory forms
     * @class InputPassword
     * @augments BasicInput
     * @classdesc Specific Password input, contains specific password validation logic. Represents input `password` element together with widget-related HTML markup.
     * HTML structure assembled on backend and injected into resulted html markup by using `formElement` component
     * and dynamic forms configuration JSON.
     * Uses 'show/hide mask' functionality, which allows customer to see/hide printed password characters.
     * Adds additional validation constraints rules, which are password-specific (like special characters, uppercased characters etc).
     * @property {string} data-widget - Widget name `inputPassword`
     * @example <caption>InputPassword definition in dynamicforms.json</caption>
     * ...
     * // fields -> input -> password
     * password: {
     *     extends: 'fields.input.base',
     *     'caption.show': true,
     *     'caption.text': 'form.profile.password.caption',
     *     'label.text': 'form.profile.password',
     *     element: {
     *         type: 'password',
     *         required: true,
     *         minLength: 8,
     *         maxLength: 255,
     *         attributes: {
     *             'data-event-input': 'onPasswordInput'
     *         }
     *     }
     * },
     * ...
     * @example <caption>Insertion of InputPassword inside ISML templates</caption>
     * <isset name="formElement" value="${require('forms/formElement')}" scope="page"/>
     * ...
     * <form>
     *     ...
     *     <isprint value="${
     *         formElement(pdict.profile.password).render()
     *     }" encoding="off"/>
     *     ...
     * </form>
     * @example <caption>Resulted HTML structure for InputPassword</caption>
     * <div data-widget="inputPassword" class="b-form_section m-required m-invalid"
     *     data-id="dwfrm_login_password" data-validation-config="... validation cinfig"
     * >
     *     <label class="b-form_section-label" for="dwfrm_login_password">
     *         <span class="b-form_section-required" aria-hidden="true">*</span>
     *         Password
     *     </label>
     *     <div class="b-input_password">
     *         <input data-ref="field" id="dwfrm_login_password" type="password" class="b-input m-invalid"
     *            aria-describedby="dwfrm_login_password-error" placeholder="" name="dwfrm_login_password" required="" value=""
     *            maxlength="255" aria-required="true" minlength="8" data-event-input="onPasswordInput"
     *            autocomplete="current-password" data-tau="login_password" data-event-blur="validate"
     *         >
     *         <button class="b-input_password-toggle_visibility" data-ref="showButton" data-event-click="toggleMask"
     *             data-button-text-show="Show" data-button-text-hide="Hide" aria-pressed="false"
     *             title="Toggle password field visibility" type="button" hidden="hidden">Show</button>
     *     </div>
     *     <div role="alert" class="b-form_section-message" data-ref="errorFeedback" id="dwfrm_login_password-error">This field is required.</div>
     * </div>
     */
    class InputPassword extends BasicInput {
        prefs() {
            return {
                upperCaseAmount: 0,
                lowerCaseAmount: 0,
                numbersAmount: 0,
                specialCharsAmount: 0,
                specialCharsList: '$%/()[]{}=?!.,-_*|+~#',
                ...super.prefs()
            };
        }

        /**
         * @description Checks if entered data is number
         * @param {string} val - entered password
         * @returns {boolean} check result
         */
        isNumber(val) {
            return '0123456789'.includes(val);
        }

        /**
         * @description Checks if entered data is simple character (not a number or special character)
         * @param {string} val - entered password
         * @returns {boolean} check result
         */
        isSimpleChar(val) {
            return !this.isNumber(val) && !this.prefs().specialCharsList.includes(val);
        }

        /**
         * @description Verifies if entered password is valid and encount all password constraints.
         * @returns {boolean} check result
         */
        isValid() {
            const val = this.getValue();

            if (!super.isValid()) {
                return false;
            }

            if (typeof val === 'string' && val) {
                const valChars = val.split('');

                if (
                    this.checkLowerCaseAmount(valChars)
                    && this.checkUpperCaseAmount(valChars)
                    && this.checkNumberAmount(valChars)
                    && this.checkSpecialCharsAmount(valChars)
                ) {
                    if (this.widgetToMatch && this.widgetToMatchOpts && this.widgetToMatch.data('getValue') !== val) {
                        this.error = this.widgetToMatchOpts.msg;
                        return false;
                    }
                } else {
                    return false;
                }
            }

            return true;
        }

        /**
         * @description Checks special characters amount in entered password as per corresponding constraint.
         * @param {string[]} valChars - array of entered password symbols
         * @returns {boolean} check results
         */
        checkSpecialCharsAmount(valChars) {
            if (this.prefs().specialCharsAmount) {
                const specialCharsList = this.prefs().specialCharsList;
                const specialCharsLetters = valChars.filter(char => specialCharsList.includes(char));
                if (specialCharsLetters.length < this.prefs().specialCharsAmount) {
                    this.error = this.prefs().validationConfig.errors.specialCharsAmount;
                    return false;
                }
            }
            return true;
        }

        /**
         * @description Checks numbers amount in entered password as per corresponding constraint.
         * @param {string[]} valChars - array of entered password symbols
         * @returns {boolean} check result
         */
        checkNumberAmount(valChars) {
            if (this.prefs().numbersAmount) {
                const numberLetters = valChars.filter(char => this.isNumber(char));
                if (numberLetters.length < this.prefs().numbersAmount) {
                    this.error = this.prefs().validationConfig.errors.numbersAmount;
                    return false;
                }
            }
            return true;
        }

        /**
         * @description Checks lowercased characters amount in entered password as per corresponding constraint.
         * @param {string[]} valChars - array of entered password symbols
         * @returns {boolean} check result
         */
        checkLowerCaseAmount(valChars) {
            if (this.prefs().lowerCaseAmount) {
                const lowerCaseLetters = valChars.filter(char => char === char.toLowerCase() && this.isSimpleChar(char));
                if (lowerCaseLetters.length < this.prefs().lowerCaseAmount) {
                    this.error = this.prefs().validationConfig.errors.lowerCaseAmount;
                    return false;
                }
            }
            return true;
        }

        /**
         * @description Checks uppercased characters amount in entered password as per corresponding constraint.
         * @param {string[]} valChars - array of entered password symbols
         * @returns {boolean} check results
         */
        checkUpperCaseAmount(valChars) {
            if (this.prefs().upperCaseAmount) {
                const upperCaseLetters = valChars.filter(char => char === char.toUpperCase() && this.isSimpleChar(char));
                if (upperCaseLetters.length < this.prefs().upperCaseAmount) {
                    this.error = this.prefs().validationConfig.errors.upperCaseAmount;
                    return false;
                }
            }
            return true;
        }

        /**
         * @description Show mask button inside password field
         * @returns {void}
         */
        showMaskButton() {
            this.ref('showButton').show();
        }

        /**
         * @description Handle click on button Show/Hide mask. Toggle type based on input type.
         * @listens dom#click
         * @returns {void}
         */
        toggleMask() {
            const input = this.ref('field');
            const inputType = input.attr('type');
            const showButton = this.ref('showButton');
            const showText = showButton.data('buttonTextShow');
            const hideText = showButton.data('buttonTextHide');

            if (inputType === 'password') {
                input.attr('type', 'text');
                showButton.attr('aria-pressed', 'true');
                showButton.setText(hideText);
            } else {
                input.attr('type', 'password');
                showButton.attr('aria-pressed', 'false');
                showButton.setText(showText);
            }
        }

        /**
         * @description Listener for `input` event.
         * Usually used to display Show/Hide mask button when entered needed amount of symbols into password field.
         * @listens dom#input
         * @param {RefElement} el event source element
         * @returns {void}
         */
        onPasswordInput(el) {
            const entered = el && el.val();
            if (entered && entered.length) {
                this.showMaskButton();
            }
        }
    }

    return InputPassword;
}
